import { LoadingOutlined } from "@ant-design/icons";
import { ButtonProps, Flex, Form, Spin, Tag } from "antd";
import { useForm } from "antd/es/form/Form";
import { DefaultOptionType } from "antd/es/select";
import { RadioChangeEvent } from "antd/lib";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputFormItem from "../../../../../components/Form/FormItem/InputFormItem";
import RadioFormItem from "../../../../../components/Form/FormItem/RadioFormItem";
import SelectFormItem from "../../../../../components/Form/FormItem/SelectFormItem";
import VisibilityAnimator from "../../../../../components/VisibilityAnimator/VisibilityAnimator";
import { ModalsContext } from "../../../../../context/modals.context";
import { useTrademarkList } from "../../../../../modules/obligee/records/records.queries";
import {
  useCreateProductionCodeRequest,
  useProductionCodeRequestFormData,
} from "../../../../../modules/productionCode/productionCode.queries";
import {
  CreateProductionCodeResponse,
  ProductionCodeRequestDetails,
  ProductionCodeRequestFormPayload,
} from "../../../../../modules/productionCode/productionCode.types";
import { DataPair, FormMode } from "../../../../../types";
import { clearNonNumericValues } from "../../../../../utils/functions";
import { mapSelectOptions } from "../../../../../utils/map4select";
import styles from "./ProductionCodeRequestForm.module.scss";

interface ProductionCodeFormProps {
  formMode: FormMode;
  handleSetSubmitButtonProps: (prop: Partial<ButtonProps>) => void;
  isLoading?: boolean;
  initialValues?: ProductionCodeRequestFormPayload;
  initialValuesDetails?: ProductionCodeRequestDetails;
  /**
   * Used to check if helper data (dropdown options, radio button options etc) can be used to map with detail id
   */
  useHelperFormData: boolean;
}

const ProductionCodeRequestForm = ({
  formMode,
  initialValues,
  isLoading,
  useHelperFormData,
  initialValuesDetails,
  handleSetSubmitButtonProps,
}: ProductionCodeFormProps) => {
  const [form] = useForm();
  const navigate = useNavigate();
  const { setError } = useContext(ModalsContext);
  const [machineCodes, setMachineCodes] = useState<DefaultOptionType[]>([]);
  const [isMachineCodeDisabled, setIsMachineCodeDisabled] = useState(true);
  const [showMachineCodeField, setShowMachineCodeField] = useState<
    boolean | undefined
  >(undefined);
  const [selectedTobaccoProductType, setSelectedTobaccoProductType] = useState<
    number | null
  >(null);
  const [selectedTipObveznikaId, setSelectedTipObveznikaId] = useState<
    number | null
  >(null);

  // #region | Queries

  const {
    mutate: createProductionCodeRequest,
    isPending: isCreatingProductionCodeRequest,
  } = useCreateProductionCodeRequest({
    onSuccess: onSuccessHandler,
    onError: onErrorHandler,
  });

  const { data: formData, isLoading: isLoadingFormData } =
    useProductionCodeRequestFormData({
      tipObveznikaId: selectedTipObveznikaId,
    });

  const { data: trademarks, isLoading: isLoadingTrademarks } = useTrademarkList(
    {
      filters: {
        vrstaProizvodaId: selectedTobaccoProductType,
        obveznikTip: selectedTipObveznikaId,
        naziv: null,
      },
    }
  );

  // #endregion | Queries

  // #region | Functions

  const handleResetFields = (
    fields?: (keyof ProductionCodeRequestFormPayload)[]
  ) => {
    if (formMode !== FormMode.DETAILS) {
      form.resetFields(fields);
      checkSubmitButtonDisabledState();
    }
  };

  function onSuccessHandler(data: CreateProductionCodeResponse | null) {
    handleResetFields();
    navigate(
      `/obveznik/produkcioniKodovi/uspesnoKreiranjeZahteva?id=${data?.id}&brojZahteva=${data?.brojZahteva}&sifraRobneMarke=${data?.sifraRobneMarke}`
    );
  }

  function onErrorHandler(error: Error | string) {
    setError(
      typeof error === "string" ? error : t("COMMON.DOSLO_JE_DO_GRESKE")
    );
  }

  const checkSubmitButtonDisabledState = () => {
    form
      .validateFields({ validateOnly: true })
      .then(() => {
        handleSetSubmitButtonProps({ disabled: false });
      })
      .catch((error) => {
        handleSetSubmitButtonProps({
          disabled: error?.errorFields?.length > 0,
        });
      });
  };

  const resetMachineCodes = () => {
    setIsMachineCodeDisabled(true);
    setMachineCodes([]);
  };

  const onTipObveznikaChangeHandler = (event: RadioChangeEvent) => {
    handleResetFields(["masinaId", "robnaMarkaId", "objekatId"]);
    resetMachineCodes();

    setSelectedTipObveznikaId(event?.target?.value);
  };

  const onTobaccoProductTypeChangeHandler = (event: RadioChangeEvent) => {
    handleResetFields(["robnaMarkaId"]);

    setSelectedTobaccoProductType(event.target.value);
  };

  const onMachineChangeHandler = (event: RadioChangeEvent) => {
    setShowMachineCodeField(event.target.value);
  };

  const handleSetMachineCodes = (objectId: number) => {
    setIsMachineCodeDisabled(
      !Boolean(objectId) || formMode === FormMode.DETAILS
    );

    handleResetFields(["masinaId"]);

    const object = formData?.objects.find(
      (object) => object.value === objectId
    );

    const mappedMachineCodes = mapSelectOptions<DataPair>({
      labelAccessor: "naziv",
      valueAccessor: "id",
      sourceArray: object?.masine ?? [],
    });

    setMachineCodes(mappedMachineCodes);
  };

  const onFinishHandler = async (payload: ProductionCodeRequestFormPayload) => {
    switch (formMode) {
      case FormMode.CREATE:
        createProductionCodeRequest(payload);
        return;
      default:
        return;
    }
  };

  // #endregion | Functions

  useEffect(() => {
    if (initialValues?.ukljuceneMasine && showMachineCodeField === undefined) {
      setShowMachineCodeField(initialValues?.ukljuceneMasine);
    }
    if (
      initialValues?.duvanskiProizvodVrstaId &&
      selectedTobaccoProductType === null
    ) {
      setSelectedTobaccoProductType(initialValues?.duvanskiProizvodVrstaId);
    }
    if (initialValues?.tipObveznikaId && selectedTipObveznikaId === null) {
      setSelectedTipObveznikaId(initialValues?.tipObveznikaId);
    }
    if (initialValues?.objekatId && !machineCodes?.length) {
      handleSetMachineCodes(initialValues?.objekatId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    form.setFieldsValue(initialValues);

    return () => {
      form.resetFields();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    handleSetSubmitButtonProps({ loading: isCreatingProductionCodeRequest });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingProductionCodeRequest]);

  return (
    <Spin
      size="large"
      indicator={<LoadingOutlined spin />}
      spinning={(isLoadingFormData && !formData) || isLoading}
    >
      <Form
        form={form}
        name="productionCodeForm"
        onFinish={onFinishHandler}
        disabled={formMode === FormMode.DETAILS}
        initialValues={initialValues}
        labelCol={{ span: 14 }}
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          gap: 10,
        }}
        labelAlign="left"
        onValuesChange={checkSubmitButtonDisabledState}
        colon={false}
      >
        {formMode === FormMode.DETAILS ? (
          <Flex gap={10} vertical>
            <div className={styles.brojZahtevaContainer}>
              <span className={styles.brojZahtevaLabel}>
                {t("TABLES.BROJ_ZAHTEVA")}:
              </span>
              <span>{initialValues?.brojZahteva}</span>
            </div>
            <div className={styles.brojZahtevaContainer}>
              <span className={styles.brojZahtevaLabel}>
                {t("TABLES.STATUS_UPLATE")}:
              </span>
              {initialValuesDetails?.placen ? (
                <Tag color="success">{t("COMMON.PLACEN")}</Tag>
              ) : (
                <Tag color="error">{t("COMMON.NIJE_PLACEN")}</Tag>
              )}
            </div>
          </Flex>
        ) : (
          <></>
        )}
        {useHelperFormData ? (
          <RadioFormItem<ProductionCodeRequestFormPayload>
            name="tipObveznikaId"
            onChange={onTipObveznikaChangeHandler}
            options={formData?.obveznikTypes}
            label={t("AKCIZNE_MARKICE.TIP_OBVEZNIKA")}
            rules={[
              {
                required: true,
                message: t("QR_CODE.TIP_OBVEZNIKA_JE_OBAVEZAN"),
              },
            ]}
          />
        ) : (
          <InputFormItem
            label={t("AKCIZNE_MARKICE.TIP_OBVEZNIKA")}
            value={initialValuesDetails?.tipObveznika}
            disabled
            required
          />
        )}

        {useHelperFormData ? (
          <SelectFormItem<ProductionCodeRequestFormPayload>
            name="objekatId"
            label={t("TABLES.OBJEKAT")}
            loading={isLoadingFormData}
            options={formData?.objects}
            placeholder={t("QR_CODE.IZABERITE_OBJEKAT")}
            onChange={(objectId) => handleSetMachineCodes(objectId)}
            rules={[
              { required: true, message: t("QR_CODE.OBJEKAT_JE_OBAVEZAN") },
            ]}
            allowClear
          />
        ) : (
          <InputFormItem
            label={t("TABLES.OBJEKAT")}
            value={initialValuesDetails?.objekatNaziv}
            disabled
            required
          />
        )}

        <RadioFormItem<ProductionCodeRequestFormPayload>
          name="ukljuceneMasine"
          onChange={onMachineChangeHandler}
          options={[
            { label: t("COMMON.DA"), value: true },
            { label: t("COMMON.NE"), value: false },
          ]}
          label={t(
            "QR_CODE.OZNAKA_JESU_LI_U_PROCES_PROIZVODNJE_UKLJUCENE_MASINE"
          )}
          rules={[
            {
              required: true,
              message: t("QR_CODE.OZNAKA_JE_OBAVEZNA"),
            },
          ]}
        />

        <VisibilityAnimator
          isVisible={Boolean(showMachineCodeField)}
          preventAnimation={formMode === FormMode.DETAILS}
          initialVisibility={false}
        >
          {useHelperFormData ? (
            <SelectFormItem<ProductionCodeRequestFormPayload>
              name="masinaId"
              label={t("QR_CODE.IDENTIFIKACIJSKI_KOD_MASINE")}
              options={machineCodes}
              loading={isLoadingFormData}
              placeholder={t("QR_CODE.IZABERITE_MASINU")}
              disabledTooltip={
                formMode !== FormMode.DETAILS
                  ? t("QR_CODE.ZA_IZBOR_MASINE_PRVO_ODABERITE_OBJEKAT")
                  : undefined
              }
              disabled={isMachineCodeDisabled}
              allowClear
            />
          ) : (
            <InputFormItem
              label={t("QR_CODE.IDENTIFIKACIJSKI_KOD_MASINE")}
              value={initialValuesDetails?.masineNaziv}
              disabled
            />
          )}
        </VisibilityAnimator>

        <RadioFormItem<ProductionCodeRequestFormPayload>
          name="duvanskiProizvodVrstaId"
          onChange={onTobaccoProductTypeChangeHandler}
          options={formData?.productTypes}
          label={t("QR_CODE.VRSTA_DUVANSKOG_PROIZVODA")}
          rules={[
            {
              required: true,
              message: t("QR_CODE.OZNAKA_JE_OBAVEZNA"),
            },
          ]}
        />

        <VisibilityAnimator
          isVisible={Boolean(selectedTobaccoProductType)}
          preventAnimation={formMode === FormMode.DETAILS}
          initialVisibility={false}
        >
          <SelectFormItem<ProductionCodeRequestFormPayload>
            name="robnaMarkaId"
            options={trademarks}
            loading={isLoadingTrademarks}
            placeholder={t("PLACEHOLDERS.IZABERI_BREND")}
            label={t("QR_CODE.ROBNA_MARKA_DUVANSOG_PROIZVODA")}
            rules={[
              {
                required: true,
                message: t("QR_CODE.ROBNA_MARKA_JE_OBAVEZNA"),
              },
            ]}
            showSearch
            allowClear
          />
        </VisibilityAnimator>

        <InputFormItem<ProductionCodeRequestFormPayload>
          name="oznakaKn"
          label={t(
            "QR_CODE.OZNAKA_KOMBINOVANE_NOMENKLATURE_KN_CARINSKA_TARIFA"
          )}
          placeholder={t(
            "QR_CODE.OZNAKA_KOMBINOVANE_NOMENKLATURE_KN_CARINSKA_TARIFA"
          )}
          rules={[{ required: true, message: t("QR_CODE.OZNAKA_JE_OBAVEZNA") }]}
        />

        <InputFormItem<ProductionCodeRequestFormPayload>
          name="prosecnaBrutoMasaJedinicnogPakovanja"
          type="number"
          min={0}
          label={t(
            "QR_CODE.PROSECNA_BRUTOMASA_JEDINICNOG_PAKOVANJA_UKLJUCUJUCI_PAKOVANJE"
          )}
          placeholder={t("QR_CODE.PROSECNA_BRUTO_MASA_JEDINICNOG_PAKOVANJA")}
          rules={[
            {
              required: true,
              message: t("QR_CODE.BRUTO_MASA_JE_OBAVEZNA"),
            },
          ]}
          step={0.1}
          onKeyDown={(event) => clearNonNumericValues(event, ["."])}
        />

        <InputFormItem<ProductionCodeRequestFormPayload>
          name="brojCigaretaUPojedinacnomPakovanju"
          type="number"
          min={0}
          label={t("QR_CODE.BROJ_CIGARETA_U_JEDINICNOM_PAKOVANJU")}
          placeholder={t("QR_CODE.BROJ_CIGARETA_U_JEDINICNOM_PAKOVANJU")}
          rules={[
            {
              required: true,
              message: t("QR_CODE.BROJ_CIGARETA_JE_OBAVEZAN"),
            },
          ]}
        />

        <InputFormItem<ProductionCodeRequestFormPayload>
          name="trazenaKolicinaJedinstvenihOznakaPojedinacnogPakovanja"
          type="number"
          min={0}
          label={t("QR_CODE.TRAZENA_KOLICINA_JEDINSTVENIH_OZNAKA")}
          placeholder={t("QR_CODE.TRAZENA_KOLICINA_JEDINSTVENIH_OZNAKA")}
          rules={[
            {
              required: true,
              message: t("QR_CODE.TRAZENA_KOLICINA_JE_OBAVEZNA"),
            },
            {
              validator: (_, value) => {
                if (value && value < 1000) {
                  return Promise.reject(
                    t("QR_CODE.MINIMALNA_KOLICINA_ZA_PORUCIVANJE_JE_1000")
                  );
                }
                if (value && value > 1000000) {
                  return Promise.reject(
                    t("QR_CODE.MAKSIMALNA_KOLICINA_ZA_PORUCIVANJE_JE_1_000_000")
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        />
      </Form>
    </Spin>
  );
};

export default ProductionCodeRequestForm;
